const content = `
# Cold-Start Prediction

Cold-start prediction generates an 8,760-hour (full-year) load forecast for a building with **zero historical meter data**. The only inputs are building metadata and weather data.

## What Is Cold-Start?

A "cold-start" building is one where Vortx has no prior energy usage data. This is common for:
- New construction projects before occupancy
- Prospective customers during sales proposals
- Newly acquired buildings with no sub-metered data

Traditional statistical forecasting requires months of historical data. Component forecasting solves cold-start by using ComStock-trained models that generalize from physical building characteristics.

## Inputs

The cold-start predictor requires two inputs:

### Building Metadata

| Field | Example | Required |
|-------|---------|----------|
| Building type | LargeOffice | Yes |
| Climate zone | 4A | Yes |
| Floor area (sqft) | 50,000 | Yes |
| Vintage (year built) | 2005 | Yes |
| Number of stories | 3 | No |
| HVAC system type | VAV with reheat | No |

### Weather Data (8760 Hours)

Hourly weather timeseries for the building's location:
- Dry-bulb temperature (the primary HVAC driver)
- Relative humidity
- Global horizontal irradiance (for lighting models)

Weather data can come from TMY3 files or actual meteorological year (AMY) data.

## How Prediction Works

1. **Cluster assignment** -- The building's metadata is matched to the nearest archetype cluster for its building type using the clustering model trained during the training phase.
2. **Feature engineering** -- Weather data is combined with calendar features (hour of day, day of week, month) and building metadata to create the feature matrix.
3. **Per-component prediction** -- Four LightGBM models (HVAC, refrigeration, plug loads, lighting) each produce an 8,760-hour timeseries.
4. **Aggregation** -- Component predictions are summed to produce the total building load forecast.

## Interpreting Results

The **Forecast** tab shows a stacked area chart with four colored bands:
- **HVAC (red)** -- Dominates in summer/winter, low in shoulder months
- **Refrigeration (blue)** -- Relatively flat if present, higher in warm months
- **Plug Loads (gold)** -- Follows occupancy schedule (business hours weekdays)
- **Lighting (yellow)** -- Follows occupancy with slight daylight sensitivity

## Accuracy Expectations

Cold-start predictions are less accurate than fine-tuned models but are designed to be "good enough" for optimization:

| Metric | Cold-Start Target |
|--------|------------------|
| Total MAPE | < 25% |
| HVAC MAPE | < 20% |
| Peak Error | < 15% |
| PoP Score | > 85% |

After 3-7 days of real meter data, fine-tuning typically improves MAPE by 5-10 percentage points.
`;

export default content;
